import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  onSnapshot,
  QuerySnapshot,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '@/config/firebase';
import { BusinessDocument, BusinessDocumentType, BusinessDocumentStatus } from '@/models/business-document.model';
import {
  auditCreate,
  auditDelete,
  auditUpdate,
  auditView,
} from '@/utils/audit-integration';
import { AuditEntityType } from '@/models/audit-log.model';

const BUSINESS_DOCUMENTS_COLLECTION = 'businessDocuments';
const STORAGE_PATH = 'business-documents';

export class BusinessDocumentService {
  /**
   * Get all business documents
   */
  static async getAllDocuments(): Promise<BusinessDocument[]> {
    const q = query(
      collection(db, BUSINESS_DOCUMENTS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToDocument(doc.id, doc.data()));
  }

  /**
   * Get document by ID
   */
  static async getDocumentById(id: string, logAccess: boolean = false): Promise<BusinessDocument | null> {
    const docRef = doc(db, BUSINESS_DOCUMENTS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    const document = this.docToDocument(docSnap.id, docSnap.data());
    
    if (logAccess) {
      auditView(AuditEntityType.document, id, document.name).catch(console.error);
    }
    
    return document;
  }

  /**
   * Get documents by type
   */
  static async getDocumentsByType(type: BusinessDocumentType): Promise<BusinessDocument[]> {
    const q = query(
      collection(db, BUSINESS_DOCUMENTS_COLLECTION),
      where('type', '==', type),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToDocument(doc.id, doc.data()));
  }

  /**
   * Get expiring documents
   */
  static async getExpiringDocuments(daysAhead: number = 30): Promise<BusinessDocument[]> {
    const allDocuments = await this.getAllDocuments();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() + daysAhead);
    
    return allDocuments.filter(doc => {
      if (!doc.expirationDate) return false;
      return doc.expirationDate <= cutoffDate && doc.expirationDate >= new Date();
    });
  }

  /**
   * Search documents
   */
  static async searchDocuments(searchTerm: string): Promise<BusinessDocument[]> {
    const allDocuments = await this.getAllDocuments();
    const lowerSearch = searchTerm.toLowerCase();
    
    return allDocuments.filter(doc =>
      doc.name.toLowerCase().includes(lowerSearch) ||
      doc.documentNumber?.toLowerCase().includes(lowerSearch) ||
      doc.description?.toLowerCase().includes(lowerSearch) ||
      doc.issuingAuthority?.toLowerCase().includes(lowerSearch) ||
      doc.tags.some(tag => tag.toLowerCase().includes(lowerSearch))
    );
  }

  /**
   * Upload file to Firebase Storage
   */
  static async uploadFile(file: File, documentId: string): Promise<string> {
    const fileExtension = file.name.split('.').pop();
    const fileName = `${documentId}.${fileExtension}`;
    const storageRef = ref(storage, `${STORAGE_PATH}/${fileName}`);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  }

  /**
   * Create business document
   */
  static async createDocument(
    documentData: Partial<BusinessDocument>,
    file?: File
  ): Promise<string> {
    const now = Timestamp.now();
    
    // First create the document to get the ID
    const data: any = {
      name: documentData.name || '',
      type: documentData.type || BusinessDocumentType.other,
      documentNumber: documentData.documentNumber || null,
      status: documentData.status || BusinessDocumentStatus.active,
      fileUrl: null,
      fileName: file?.name || null,
      fileSize: file?.size || null,
      mimeType: file?.type || null,
      issueDate: documentData.issueDate ? Timestamp.fromDate(documentData.issueDate) : null,
      expirationDate: documentData.expirationDate ? Timestamp.fromDate(documentData.expirationDate) : null,
      renewalDate: documentData.renewalDate ? Timestamp.fromDate(documentData.renewalDate) : null,
      description: documentData.description || null,
      issuingAuthority: documentData.issuingAuthority || null,
      notes: documentData.notes || null,
      tags: documentData.tags || [],
      metadata: documentData.metadata || {},
      createdBy: documentData.createdBy || '',
      createdByName: documentData.createdByName || '',
      createdAt: now,
      updatedAt: now,
      lastReviewedAt: null,
      lastReviewedBy: null,
    };

    const docRef = await addDoc(collection(db, BUSINESS_DOCUMENTS_COLLECTION), data);
    const documentId = docRef.id;

    // Upload file if provided
    if (file) {
      try {
        const downloadURL = await this.uploadFile(file, documentId);
        await updateDoc(docRef, {
          fileUrl: downloadURL,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
        });
      } catch (error) {
        console.error('Error uploading file:', error);
        // Document is still created, just without file
      }
    }

    auditCreate(AuditEntityType.document, documentId, documentData.name || 'Business Document', {
      type: documentData.type,
      documentNumber: documentData.documentNumber,
    }).catch(console.error);

    return documentId;
  }

  /**
   * Update business document
   */
  static async updateDocument(
    id: string,
    updates: Partial<BusinessDocument>,
    file?: File
  ): Promise<void> {
    const docRef = doc(db, BUSINESS_DOCUMENTS_COLLECTION, id);
    const updateData: any = {
      updatedAt: Timestamp.now(),
    };

    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.type !== undefined) updateData.type = updates.type;
    if (updates.documentNumber !== undefined) updateData.documentNumber = updates.documentNumber;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.issueDate !== undefined) {
      updateData.issueDate = updates.issueDate ? Timestamp.fromDate(updates.issueDate) : null;
    }
    if (updates.expirationDate !== undefined) {
      updateData.expirationDate = updates.expirationDate ? Timestamp.fromDate(updates.expirationDate) : null;
    }
    if (updates.renewalDate !== undefined) {
      updateData.renewalDate = updates.renewalDate ? Timestamp.fromDate(updates.renewalDate) : null;
    }
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.issuingAuthority !== undefined) updateData.issuingAuthority = updates.issuingAuthority;
    if (updates.notes !== undefined) updateData.notes = updates.notes;
    if (updates.tags !== undefined) updateData.tags = updates.tags;
    if (updates.metadata !== undefined) updateData.metadata = updates.metadata;

    // Upload new file if provided
    if (file) {
      try {
        // Delete old file if exists
        const existingDoc = await this.getDocumentById(id);
        if (existingDoc?.fileUrl) {
          try {
            const oldFileRef = ref(storage, existingDoc.fileUrl);
            await deleteObject(oldFileRef);
          } catch (error) {
            console.error('Error deleting old file:', error);
            // Continue even if old file deletion fails
          }
        }

        const downloadURL = await this.uploadFile(file, id);
        updateData.fileUrl = downloadURL;
        updateData.fileName = file.name;
        updateData.fileSize = file.size;
        updateData.mimeType = file.type;
      } catch (error) {
        console.error('Error uploading file:', error);
        throw new Error('Failed to upload file');
      }
    }

    await updateDoc(docRef, updateData);

    auditUpdate(AuditEntityType.document, id, updates.name || 'Business Document').catch(console.error);
  }

  /**
   * Delete business document
   */
  static async deleteDocument(id: string): Promise<void> {
    const document = await this.getDocumentById(id);
    
    // Delete file from storage if exists
    if (document?.fileUrl) {
      try {
        const fileRef = ref(storage, document.fileUrl);
        await deleteObject(fileRef);
      } catch (error) {
        console.error('Error deleting file from storage:', error);
        // Continue with document deletion even if file deletion fails
      }
    }

    const docRef = doc(db, BUSINESS_DOCUMENTS_COLLECTION, id);
    await deleteDoc(docRef);

    auditDelete(AuditEntityType.document, id, document?.name).catch(console.error);
  }

  /**
   * Subscribe to documents changes
   */
  static subscribeToDocuments(
    callback: (documents: BusinessDocument[]) => void
  ): () => void {
    const q = query(
      collection(db, BUSINESS_DOCUMENTS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    
    return onSnapshot(q, (snapshot: QuerySnapshot) => {
      const documents = snapshot.docs.map(doc =>
        this.docToDocument(doc.id, doc.data())
      );
      callback(documents);
    });
  }

  /**
   * Convert Firestore document to BusinessDocument
   */
  private static docToDocument(id: string, data: any): BusinessDocument {
    return {
      id,
      name: data.name || '',
      type: data.type || BusinessDocumentType.other,
      documentNumber: data.documentNumber,
      status: data.status || BusinessDocumentStatus.active,
      fileUrl: data.fileUrl,
      fileName: data.fileName,
      fileSize: data.fileSize,
      mimeType: data.mimeType,
      issueDate: data.issueDate?.toDate(),
      expirationDate: data.expirationDate?.toDate(),
      renewalDate: data.renewalDate?.toDate(),
      description: data.description,
      issuingAuthority: data.issuingAuthority,
      notes: data.notes,
      tags: data.tags || [],
      metadata: data.metadata || {},
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      lastReviewedAt: data.lastReviewedAt?.toDate(),
      lastReviewedBy: data.lastReviewedBy,
    };
  }
}
